import { createPopper, Instance, Options } from '@popperjs/core';
import { ReactNode, useEffect, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import { classNames } from '../../utils';

import './Tooltip.css';

interface TooltipProps {
  /** The element that will trigger the tooltip on hover/focus */
  children: ReactNode;
  /** The content displayed inside the tooltip */
  text: ReactNode;
  /** Placement direction for the tooltip (e.g., 'top', 'bottom', 'left', 'right') */
  placement?: Options['placement'];
  /** Additional class name(s) for the tooltip */
  tooltipClassName?: string;
  /** Additional class name(s) for the tooltip wrapper */
  wrapperClassName?: string;
  /** Whether to hide the arrow */
  hideArrow?: boolean;
  /** Whether the tooltip is disabled */
  disabled?: boolean;
}

const Tooltip = ({
  children,
  text: content,
  placement = 'bottom',
  tooltipClassName,
  wrapperClassName,
  hideArrow,
  disabled,
}: TooltipProps) => {
  const [visible, setVisible] = useState(false);

  // Track hover states for the trigger and the tooltip
  const [isHoveringTrigger, setIsHoveringTrigger] = useState(false);
  const [isHoveringTooltip, setIsHoveringTooltip] = useState(false);

  // References for the trigger (referenceRef), tooltip (popperRef), and arrow
  const referenceRef = useRef<HTMLDivElement | null>(null);
  const popperRef = useRef<HTMLDivElement | null>(null);
  const arrowRef = useRef<HTMLDivElement | null>(null);

  // Keep track of the Popper instance
  const popperInstanceRef = useRef<Instance | null>(null);

  // Initialize Popper once references are available
  useEffect(() => {
    if (referenceRef.current && popperRef.current) {
      popperInstanceRef.current = createPopper(
        referenceRef.current,
        popperRef.current,
        {
          placement,
          strategy: 'absolute', // or 'fixed' if you prefer
          modifiers: [
            {
              name: 'offset',
              options: {
                offset: [0, 8],
              },
            },
            ...(hideArrow
              ? []
              : [
                  {
                    name: 'arrow',
                    options: {
                      element: arrowRef.current,
                      padding: 5,
                    },
                  },
                ]),
            {
              // This ensures the boundary is the viewport (entire screen)
              // so the tooltip won't be clipped by any parent container.
              name: 'preventOverflow',
              options: {
                boundary: 'viewport',
                // or boundary: 'window'
              },
            },
          ],
        },
      );
    }

    return () => {
      // Cleanup on unmount
      popperInstanceRef.current?.destroy();
      popperInstanceRef.current = null;
    };
  }, [hideArrow, placement]);

  // Whenever hover states change, decide if the tooltip should be visible
  useEffect(() => {
    // If hovering trigger OR hovering tooltip, show it
    setVisible(!disabled && (isHoveringTrigger || isHoveringTooltip));
  }, [isHoveringTrigger, isHoveringTooltip, disabled]);

  // Update popper when tooltip becomes visible or content changes
  useEffect(() => {
    if (visible) {
      popperInstanceRef.current?.update();
    }
  }, [visible, content]);

  // The tooltip element that we'll portal to <body>
  const tooltipElement = (
    <div
      ref={popperRef}
      role="tooltip"
      // Conditional Tailwind classes
      className={classNames(
        'tooltip',
        visible
          ? 'scale-100 opacity-100'
          : 'pointer-events-none scale-95 opacity-0',
        'absolute z-50 box-border origin-center transform rounded-md bg-gray-800 p-2 text-sm text-white shadow-md transition-all duration-150 ease-in-out',
        tooltipClassName,
      )}
      onMouseEnter={() => setIsHoveringTooltip(true)}
      onMouseLeave={() => setIsHoveringTooltip(false)}
      style={{ maxWidth: '90vw' }}
    >
      {content}

      {/* Arrow */}
      {!hideArrow && <div ref={arrowRef} data-popper-arrow className="arrow" />}
    </div>
  );

  return (
    <>
      {/* The trigger (span wrapping the children) */}
      <div
        ref={referenceRef}
        onMouseEnter={() => setIsHoveringTrigger(true)}
        onMouseLeave={() => setIsHoveringTrigger(false)}
        className={classNames('cursor-pointer', wrapperClassName)}
      >
        {children}
      </div>

      {/* Render the tooltip via a portal to document.body */}
      {ReactDOM.createPortal(tooltipElement, document.body)}
    </>
  );
};

export default Tooltip;
